import React from 'react';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Link,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MessageIcon from '@mui/icons-material/Message';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { formatTime } from './briefingUtils';

function MABriefingView({ data, onClose, onViewPreSession, navigate, onClientClick, isMobile }) {
  const { providers = [], ma_appointments = {}, total_np_sessions = 0, total_np_completed = 0 } = data;

  const handleClientClick = (link) => {
    if (onClientClick) {
      onClientClick(link);
      return;
    }
    navigate(link);
    onClose();
  };

  return (
    <Box sx={{ maxHeight: isMobile ? '85vh' : 450, overflow: 'auto' }}>
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        bgcolor: 'background.paper',
        zIndex: 1,
      }}>
        <Typography variant="h6" fontWeight={600}>
          MA Briefing
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {total_np_completed}/{total_np_sessions} NP sessions completed today
        </Typography>
        {ma_appointments.total_sessions > 0 && (
          <Typography variant="body2" color="text.secondary">
            Your appointments: {ma_appointments.completed_sessions}/{ma_appointments.total_sessions} completed
          </Typography>
        )}
      </Box>

      {providers.map((provider) => {
        const briefing = provider.briefing || {};
        const sessions = briefing.all_sessions || [];
        const nextSession = briefing.next_session;
        return (
          <Box key={provider.provider_id}>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {provider.provider_name}
                </Typography>
                <Chip
                  icon={<CheckCircleIcon fontSize="small" />}
                  label={`${briefing.completed_sessions || 0}/${briefing.total_sessions || 0}`}
                  size="small"
                  color={(briefing.completed_sessions || 0) === (briefing.total_sessions || 0) && briefing.total_sessions > 0 ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>

              {nextSession && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  mb: 1,
                }}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Next: {nextSession.client_initials} at {formatTime(nextSession.start_time, nextSession.timezone)}
                  </Typography>
                  {nextSession.starts_in_minutes <= 15 && nextSession.starts_in_minutes >= 0 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => onViewPreSession(nextSession.encounter_id)}
                      sx={{ textTransform: 'none', ml: 'auto' }}
                    >
                      View
                    </Button>
                  )}
                </Box>
              )}

              {sessions.length > 0 && (
                <List dense disablePadding>
                  {sessions.map((session) => (
                    <ListItem key={session.encounter_id} disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => handleClientClick(`/calendar?encounter=${session.encounter_id}`)}
                        sx={{ py: 0.25, px: 1, borderRadius: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          {session.status === 'completed' ? (
                            <CheckCircleIcon sx={{ fontSize: 14 }} color="success" />
                          ) : (
                            <AccessTimeIcon sx={{ fontSize: 14 }} color="primary" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {session.client_initials} - {formatTime(session.start_time, session.timezone)}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}

              {(briefing.alerts?.secure_messages?.length > 0 || briefing.alerts?.health_tracking?.length > 0) && (
                <Box sx={{ mt: 1 }}>
                  {briefing.alerts.secure_messages?.map((msg, idx) => (
                    <Box key={`msg-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <MessageIcon sx={{ fontSize: 14 }} color="primary" />
                      <Link
                        component="button"
                        variant="caption"
                        onClick={() => handleClientClick(msg.link)}
                        sx={{ textAlign: 'left' }}
                      >
                        {msg.client_initials}: {msg.summary}
                      </Link>
                    </Box>
                  ))}
                  {briefing.alerts.health_tracking?.map((alert, idx) => (
                    <Box key={`health-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <TrendingDownIcon sx={{ fontSize: 14 }} color="warning" />
                      <Link
                        component="button"
                        variant="caption"
                        onClick={() => handleClientClick(alert.link)}
                        sx={{ textAlign: 'left' }}
                      >
                        {alert.client_initials}: {alert.summary}
                      </Link>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}

      {providers.length === 0 && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No provider assignments found.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default MABriefingView;
